import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import ClientNav from '@/components/ClientNav';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UserPlus, Key, Trash2, Edit, Users, Shield, Code, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminUsers = () => {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'developer' });
  const [newPassword, setNewPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/users`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User created successfully!');
      setCreateOpen(false);
      setFormData({ name: '', email: '', password: '', role: 'developer' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setSubmitting(true);
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}`, {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        active: formData.active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User updated successfully!');
      setEditOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/users/${selectedUser.id}/reset-password`, 
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Password reset successfully!');
      setPasswordOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted successfully!');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name || '',
      email: user.email,
      role: user.role,
      active: user.status === 'active'
    });
    setEditOpen(true);
  };

  const openPasswordReset = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setPasswordOpen(true);
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Shield size={16} className="text-red-400" />;
      case 'developer': return <Code size={16} className="text-blue-400" />;
      case 'outreach': return <Megaphone size={16} className="text-green-400" />;
      default: return <Users size={16} className="text-slate-400" />;
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-red-500/10 text-red-400 border-red-500/20',
      developer: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      outreach: 'bg-green-500/10 text-green-400 border-green-500/20',
      client: 'bg-purple-500/10 text-purple-400 border-purple-500/20'
    };
    return colors[role] || 'bg-slate-500/10 text-slate-400 border-slate-500/20';
  };

  const staffUsers = users.filter(u => ['admin', 'developer', 'outreach'].includes(u.role));
  const clientUsers = users.filter(u => u.role === 'client');

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712]">
        <ClientNav />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712]" data-testid="admin-users-page">
      <ClientNav />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold font-['Syne'] text-white mb-2" data-testid="users-title">
              User Management
            </h1>
            <p className="text-slate-400">Manage staff accounts and permissions</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-white text-black hover:bg-slate-200" data-testid="create-user-btn">
                <UserPlus size={18} className="mr-2" />
                Create Staff User
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-950 border-slate-800 text-white">
              <DialogHeader>
                <DialogTitle>Create Staff User</DialogTitle>
                <DialogDescription className="text-slate-400">
                  Add a new admin, developer, or outreach team member
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 py-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Full Name</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="John Doe"
                    required
                    className="bg-slate-900 border-slate-800 text-white"
                    data-testid="user-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Email Address</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="user@dhhomenet.co.uk"
                    required
                    className="bg-slate-900 border-slate-800 text-white"
                    data-testid="user-email-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Password</label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="Strong password"
                    required
                    className="bg-slate-900 border-slate-800 text-white"
                    data-testid="user-password-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Role</label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                    <SelectTrigger className="bg-slate-900 border-slate-800" data-testid="role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 text-white">
                      <SelectItem value="admin">Admin - Full Access</SelectItem>
                      <SelectItem value="developer">Developer - Build & Deploy</SelectItem>
                      <SelectItem value="outreach">Outreach - Client Management</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-indigo-500 hover:bg-indigo-600 text-white"
                  data-testid="submit-create-user"
                >
                  {submitting ? 'Creating...' : 'Create User'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Staff Users Section */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
            <Shield size={20} className="mr-2 text-indigo-400" />
            Staff Users ({staffUsers.length})
          </h2>
          {staffUsers.length === 0 ? (
            <div className="bg-slate-950 border border-slate-800 rounded-xl p-8 text-center">
              <Users className="w-12 h-12 text-slate-700 mx-auto mb-3" />
              <p className="text-slate-400">No staff users yet</p>
            </div>
          ) : (
            <div className="bg-slate-950 border border-slate-800 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-900">
                  <tr>
                    <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">User</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Role</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Status</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Created</th>
                    <th className="text-right py-4 px-6 text-sm font-medium text-slate-300">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {staffUsers.map((user, index) => (
                    <tr key={user.id} className="border-t border-slate-800" data-testid={`staff-row-${index}`}>
                      <td className="py-4 px-6">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center mr-3">
                            {getRoleIcon(user.role)}
                          </div>
                          <div>
                            <div className="text-white font-medium">{user.name || 'No name'}</div>
                            <div className="text-slate-400 text-sm">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getRoleBadge(user.role)}`}>
                          {user.role.toUpperCase()}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          user.status === 'active' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                        }`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-slate-400 text-sm">
                        {user.invited_at ? format(new Date(user.invited_at), 'MMM dd, yyyy') : '-'}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit(user)}
                            className="text-slate-400 hover:text-white"
                            data-testid={`edit-user-${index}`}
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPasswordReset(user)}
                            className="text-slate-400 hover:text-white"
                            data-testid={`reset-password-${index}`}
                          >
                            <Key size={16} />
                          </Button>
                          {user.id !== currentUser?.id && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-400 hover:text-red-300"
                                  data-testid={`delete-user-${index}`}
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-slate-950 border-slate-800">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-white">Delete User?</AlertDialogTitle>
                                  <AlertDialogDescription className="text-slate-400">
                                    This will permanently delete {user.email}. This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="bg-slate-800 text-white hover:bg-slate-700">Cancel</AlertDialogCancel>
                                  <AlertDialogAction 
                                    onClick={() => handleDelete(user.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Client Users Section */}
        <div>
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
            <Users size={20} className="mr-2 text-purple-400" />
            Client Accounts ({clientUsers.length})
          </h2>
          {clientUsers.length === 0 ? (
            <div className="bg-slate-950 border border-slate-800 rounded-xl p-8 text-center">
              <Users className="w-12 h-12 text-slate-700 mx-auto mb-3" />
              <p className="text-slate-400">No client accounts yet</p>
            </div>
          ) : (
            <div className="bg-slate-950 border border-slate-800 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-900">
                  <tr>
                    <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Client</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Plan</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Status</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Has Password</th>
                    <th className="text-right py-4 px-6 text-sm font-medium text-slate-300">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clientUsers.map((user, index) => (
                    <tr key={user.id} className="border-t border-slate-800" data-testid={`client-row-${index}`}>
                      <td className="py-4 px-6">
                        <div>
                          <div className="text-white font-medium">{user.name || user.email}</div>
                          <div className="text-slate-400 text-sm">{user.email}</div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-500/10 text-purple-400">
                          {user.plan_id || 'starter'}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          user.status === 'active' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                        }`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          user.password_hash ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'
                        }`}>
                          {user.password_hash ? 'Yes' : 'Magic Link Only'}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPasswordReset(user)}
                            className="text-slate-400 hover:text-white"
                            title="Set/Reset Password"
                          >
                            <Key size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-slate-950 border-slate-800 text-white">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription className="text-slate-400">
              Update user details and permissions
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Full Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="bg-slate-900 border-slate-800 text-white"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Email Address</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="bg-slate-900 border-slate-800 text-white"
              />
            </div>
            {selectedUser?.role !== 'client' && (
              <div>
                <label className="text-sm font-medium mb-2 block">Role</label>
                <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                  <SelectTrigger className="bg-slate-900 border-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-white">
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="developer">Developer</SelectItem>
                    <SelectItem value="outreach">Outreach</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-indigo-500 hover:bg-indigo-600 text-white"
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordOpen} onOpenChange={setPasswordOpen}>
        <DialogContent className="bg-slate-950 border-slate-800 text-white">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription className="text-slate-400">
              Set a new password for {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">New Password</label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="bg-slate-900 border-slate-800 text-white"
                data-testid="new-password-input"
              />
            </div>
            <Button
              onClick={handleResetPassword}
              disabled={submitting || !newPassword}
              className="w-full bg-indigo-500 hover:bg-indigo-600 text-white"
              data-testid="submit-reset-password"
            >
              {submitting ? 'Resetting...' : 'Reset Password'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
